import { Box, Tabs, Tab } from '@mui/material';
import { FC, ReactNode, SyntheticEvent, useState } from 'react';
import { useContainerWidth } from '@lendica/hooks';
import { AccountContainer } from './AccountContainer';
import { useAccountDataContext } from './AccountData';
import { Profile } from './views/Profile';
import { BankAccount } from './views/BankAccount';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    const { isNarrow } = useContainerWidth();

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`nav-tabpanel-${index}`}
            aria-labelledby={`nav-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        pl: isNarrow ? 2 : 1.5,
                        pr: isNarrow ? 2 : 3,
                        pt: isNarrow ? 2 : 0,
                        maxWidth: 600,
                    }}
                >
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        'id': `nav-tab-${index}`,
        'aria-controls': `nav-tabpanel-${index}`,
    };
}

export interface AccountViewProps {
    env: string;
    placeholder?: ReactNode;
}

export const AccountView: FC<AccountViewProps> = ({ env, placeholder }) => {
    const { isNarrow } = useContainerWidth();
    const { data } = useAccountDataContext();
    const company_id = data!.company ? data!.company!.id : '';

    const [tabValue, setTabValue] = useState(0);
    const handleChange = (event: SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    return (
        <AccountContainer>
            <Tabs
                value={tabValue}
                onChange={handleChange}
                variant="fullWidth"
                aria-label="account tabs"
                textColor="secondary"
                indicatorColor="secondary"
                orientation={isNarrow ? 'horizontal' : 'vertical'}
                scrollButtons={isNarrow ? 'auto' : false}
                allowScrollButtonsMobile
                sx={theme => ({
                    minWidth: isNarrow ? 'auto' : 160,
                    width: isNarrow ? '100%' : 'auto',
                    borderRight: isNarrow ? 'none' : `1px solid ${theme.palette.divider}`,
                    borderBottom: isNarrow ? `1px solid ${theme.palette.divider}` : 'none',
                    py: isNarrow ? 0 : 2,
                })}
            >
                <Tab
                    label="Profile"
                    {...a11yProps(0)}
                    sx={{
                        textAlign: isNarrow ? 'center' : 'right',
                        alignItems: isNarrow ? 'center' : 'flex-end',
                    }}
                />
                <Tab
                    label="Bank Account"
                    {...a11yProps(1)}
                    sx={{
                        textAlign: isNarrow ? 'center' : 'right',
                        alignItems: isNarrow ? 'center' : 'flex-end',
                    }}
                />
            </Tabs>
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                }}
            >
                <TabPanel value={tabValue} index={0}>
                    <Profile />
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                    <BankAccount
                        company_id={company_id}
                        env={env}
                        company={data!.company!}
                        dataConnections={data!.dataConnections!}
                    />
                </TabPanel>
            </Box>
        </AccountContainer>
    );
};
